/* Peachy — global behaviour
   Custom elements only, no dependencies. Every define() is guarded so sections
   can load this file defensively without redefinition errors. */

(function () {
  'use strict';

  const P = (window.Peachy = window.Peachy || {});
  P.routes = P.routes || { root: '/' };
  P.strings = P.strings || {};

  /* ---------------- utilities ---------------- */

  P.fetchConfig = function (type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
    };
  };

  P.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const formatString = format || P.moneyFormat || '${{amount}}';

    function defaultTo(value, def) {
      return value == null || value !== value ? def : value;
    }

    function withDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const cents_ = parts[1] ? decimal + parts[1] : '';
      return dollars + cents_;
    }

    let value = '';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholder);
    switch (match && match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholder, value);
  };

  P.announce = function (message) {
    const region = document.getElementById('a11y-live-region');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  };

  P.debounce = function (fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  };

  const FOCUSABLE =
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';

  P.getFocusable = function (container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
  };

  /* The focusable set is re-queried on every Tab, because drawer contents are
     replaced by section rendering and <details> reveal new links after opening —
     a list captured at open time goes stale and focus walks out. */
  P.trapFocus = function (container, elementToFocus) {
    function handler(event) {
      if (event.key !== 'Tab') return;

      const focusable = P.getFocusable(container);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const index = focusable.indexOf(active);

      // Active element is outside the trap, or is a tabindex="-1" heading that
      // isn't in the focusable list: pull focus back to the appropriate edge.
      if (!container.contains(active) || index === -1) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    P.removeTrapFocus(container);
    container.__trapHandler = handler;
    document.addEventListener('keydown', handler);
    const fallback = P.getFocusable(container)[0];
    (elementToFocus || fallback || container).focus({ preventScroll: true });
  };

  P.removeTrapFocus = function (container) {
    if (container && container.__trapHandler) {
      document.removeEventListener('keydown', container.__trapHandler);
      container.__trapHandler = null;
    }
  };

  let scrollLocks = 0;
  P.lockScroll = function () {
    scrollLocks += 1;
    document.body.classList.add('drawer-open');
  };
  P.unlockScroll = function () {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.body.classList.remove('drawer-open');
  };

  P.prefersReducedMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  P.scrollBehavior = function () {
    return P.prefersReducedMotion() ? 'auto' : 'smooth';
  };

  P.define = function (name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  /* ---------------- base overlay ---------------- */

  class Overlay extends HTMLElement {
    constructor() {
      super();
      this.openerEl = null;
      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };
    }

    connectedCallback() {
      /* Delegated, not bound per element: the drawer's contents (including its close
         button) are replaced wholesale by cart section rendering, which would leave
         directly-bound handlers attached to detached nodes. */
      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-overlay-close]')) return;
        event.preventDefault();
        this.close();
      });
    }

    open(opener) {
      this.openerEl = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      P.lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      const panel = this.querySelector('[data-overlay-panel]') || this;
      window.requestAnimationFrame(() => {
        P.trapFocus(panel, panel.querySelector('[data-overlay-focus]'));
      });
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      P.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      P.removeTrapFocus(this.querySelector('[data-overlay-panel]') || this);
      if (this.openerEl && document.body.contains(this.openerEl)) {
        this.openerEl.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }

    toggle(opener) {
      this.classList.contains('is-open') ? this.close() : this.open(opener);
    }
  }

  P.Overlay = Overlay;
  P.define('peachy-overlay', class extends Overlay {});

  /* Buttons that open any overlay by id */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-overlay-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.getAttribute('data-overlay-open'));
    if (!target || typeof target.open !== 'function') return;
    event.preventDefault();
    target.open(trigger);
  });

  /* ---------------- navigation dropdown ----------------
     Click/keyboard driven (hover is a convenience, never the only way in). */

  P.define(
    'nav-dropdown',
    class NavDropdown extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-nav-toggle]');
        this.panel = this.querySelector('[data-nav-panel]');
        if (!this.toggle || !this.panel) return;

        this.parentItem = this.closest('.header__item') || this;
        this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));
        this.addEventListener('keydown', (event) => this.onKeydown(event));

        /* Pointer affordance — only on devices that genuinely hover */
        if (window.matchMedia('(hover: hover)').matches) {
          this.parentItem.addEventListener('mouseenter', () => this.setOpen(true));
          this.parentItem.addEventListener('mouseleave', () => {
            if (!this.contains(document.activeElement)) this.setOpen(false);
          });
        }

        this.onDocumentClick = (event) => {
          if (this.isOpen && !this.contains(event.target)) this.setOpen(false);
        };
        document.addEventListener('click', this.onDocumentClick);

        this.addEventListener('focusout', () => {
          window.requestAnimationFrame(() => {
            if (!this.contains(document.activeElement)) this.setOpen(false);
          });
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      get isOpen() {
        return this.toggle.getAttribute('aria-expanded') === 'true';
      }

      setOpen(state) {
        this.toggle.setAttribute('aria-expanded', state ? 'true' : 'false');
        this.panel.hidden = !state;
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.isOpen) {
          event.stopPropagation();
          this.setOpen(false);
          this.toggle.focus();
        }
        if (event.key === 'ArrowDown' && document.activeElement === this.toggle) {
          event.preventDefault();
          this.setOpen(true);
          const first = this.panel.querySelector('a[href]');
          if (first) first.focus();
        }
      }
    }
  );

  /* ---------------- sticky header ---------------- */

  P.define(
    'sticky-header',
    class StickyHeader extends HTMLElement {
      connectedCallback() {
        this.headerHeight = this.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
        this.observer = new IntersectionObserver(
          ([entry]) => this.classList.toggle('is-stuck', entry.intersectionRatio < 1),
          { threshold: [1], rootMargin: '-1px 0px 0px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }
    }
  );

  /* ---------------- announcement rotator ---------------- */

  P.define(
    'announcement-rotator',
    class AnnouncementRotator extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.announcement__item'));
        if (this.items.length < 2) return;
        this.index = 0;
        this.speed = Math.max(5000, parseInt(this.dataset.speed || '5000', 10));
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.reduced) return;
        this.start();
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => { if (!this.paused) this.start(); });
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => { if (!this.paused) this.start(); });

        this.toggleButton = this.querySelector('[data-rotator-toggle]');
        if (!this.toggleButton) return;
        this.toggleButton.addEventListener('click', () => {
          this.paused = !this.paused;
          this.paused ? this.stop() : this.start();
          this.toggleButton.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
          this.toggleButton.setAttribute(
            'aria-label',
            this.paused ? P.strings.play || 'Play' : P.strings.pause || 'Pause'
          );
        });
      }

      start() {
        this.stop();
        this.timer = setInterval(() => this.next(), this.speed);
      }

      stop() {
        if (this.timer) clearInterval(this.timer);
      }

      next() {
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }

      disconnectedCallback() {
        this.stop();
      }
    }
  );

  /* ---------------- countdown ---------------- */

  P.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      connectedCallback() {
        const target = this.dataset.deadline;
        this.deadline = target ? new Date(target.replace(' ', 'T')).getTime() : NaN;
        if (isNaN(this.deadline)) {
          this.hidden = true;
          return;
        }
        this.units = {
          days: this.querySelector('[data-days]'),
          hours: this.querySelector('[data-hours]'),
          minutes: this.querySelector('[data-minutes]'),
          seconds: this.querySelector('[data-seconds]')
        };
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      tick() {
        const diff = this.deadline - Date.now();
        if (diff <= 0) {
          clearInterval(this.timer);
          this.finish();
          return;
        }
        const s = Math.floor(diff / 1000);
        this.set('days', Math.floor(s / 86400));
        this.set('hours', Math.floor((s % 86400) / 3600));
        this.set('minutes', Math.floor((s % 3600) / 60));
        this.set('seconds', s % 60);
      }

      set(unit, value) {
        const el = this.units[unit];
        if (el) el.textContent = String(value).padStart(2, '0');
      }

      finish() {
        if (this.dataset.onEnd === 'hide') {
          const section = this.closest('.shopify-section') || this;
          section.hidden = true;
          return;
        }
        this.innerHTML = `<span class="countdown__ended">${
          this.dataset.endedText || P.strings.offerEnded || ''
        }</span>`;
      }

      disconnectedCallback() {
        if (this.timer) clearInterval(this.timer);
      }
    }
  );

  /* ---------------- quantity input ---------------- */

  P.define(
    'quantity-input',
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        if (!this.input) return;
        for (const button of this.querySelectorAll('button')) {
          button.addEventListener('click', (event) => this.onButtonClick(event));
        }
        this.input.addEventListener('change', () => this.validate());
        this.validate();
      }

      onButtonClick(event) {
        event.preventDefault();
        const step = event.currentTarget.name === 'plus' ? 1 : -1;
        const stepSize = parseInt(this.input.step || '1', 10) || 1;
        const current = parseInt(this.input.value || '1', 10) || 1;
        this.input.value = String(current + step * stepSize);
        this.validate();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      validate() {
        const min = this.input.min ? parseInt(this.input.min, 10) : 1;
        const max = this.input.max ? parseInt(this.input.max, 10) : null;
        let value = parseInt(this.input.value || min, 10);
        if (isNaN(value)) value = min;
        if (value < min) value = min;
        if (max !== null && value > max) value = max;
        this.input.value = String(value);
        const minus = this.querySelector('button[name="minus"]');
        const plus = this.querySelector('button[name="plus"]');
        if (minus) minus.disabled = value <= min;
        if (plus) plus.disabled = max !== null && value >= max;
      }
    }
  );

  /* ---------------- carousel ---------------- */

  P.define(
    'carousel-slider',
    class CarouselSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;
        this.prevBtn = this.querySelector('[data-carousel-prev]');
        this.nextBtn = this.querySelector('[data-carousel-next]');
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollBy(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollBy(1));
        this.track.addEventListener('scroll', P.debounce(() => this.update(), 100));
        window.addEventListener('resize', P.debounce(() => this.update(), 200));
        this.update();
      }

      get step() {
        const first = this.track.firstElementChild;
        if (!first) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap || '16') || 16;
        return first.getBoundingClientRect().width + gap;
      }

      scrollBy(direction) {
        const button = direction < 0 ? this.prevBtn : this.nextBtn;
        if (button?.getAttribute('aria-disabled') === 'true') return;
        this.track.scrollBy({ left: this.step * direction, behavior: P.scrollBehavior() });
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        /* aria-disabled, not disabled: a disabled button drops focus to <body>
           the moment the shopper reaches either end of the track. */
        this.prevBtn?.setAttribute('aria-disabled', this.track.scrollLeft <= 2 ? 'true' : 'false');
        this.nextBtn?.setAttribute('aria-disabled', this.track.scrollLeft >= max ? 'true' : 'false');
        const overflows = this.track.scrollWidth > this.track.clientWidth + 4;
        this.classList.toggle('carousel--static', !overflows);
      }
    }
  );

  /* ---------------- carousel dots ---------------- */
  /* Opt-in: a carousel only gets dots when a container is present for them, so
     this cannot disturb the carousels that use arrows alone. */
  function buildDots(track, dots) {
    const slides = Array.from(track.children);
    if (slides.length < 2) return;
    dots.textContent = '';
    slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'added__dot';
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dot.setAttribute('aria-label', `${index + 1}`);
      dot.addEventListener('click', () => {
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: P.scrollBehavior() });
      });
      dots.appendChild(dot);
    });
    const sync = () => {
      const width = track.scrollWidth / slides.length || 1;
      const active = Math.round(track.scrollLeft / width);
      Array.from(dots.children).forEach((dot, index) => {
        dot.setAttribute('aria-current', index === active ? 'true' : 'false');
      });
    };
    track.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  function initDots() {
    for (const dots of document.querySelectorAll('#AddedDots')) {
      const track = document.getElementById('AddedTrack');
      if (track && !dots.dataset.built) {
        dots.dataset.built = 'true';
        buildDots(track, dots);
      }
    }
  }

  document.addEventListener('DOMContentLoaded', initDots);
  document.addEventListener('peachy:cart:updated', initDots);
  if (document.readyState !== 'loading') initDots();

  /* ---------------- marquee ---------------- */

  P.define(
    'marquee-strip',
    class MarqueeStrip extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-marquee-track]');
        if (!this.track || this.track.dataset.cloned === 'true') return;
        /* Wrap the track in an animated scroller and put an aria-hidden clone
           beside it, so the loop is seamless, the content is announced once, and
           the two tracks sit in a row rather than stacking. */
        const scroller = document.createElement('div');
        scroller.className = 'marquee__scroller';
        this.track.parentNode.insertBefore(scroller, this.track);
        scroller.appendChild(this.track);

        const clone = this.track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-marquee-track');
        clone.classList.add('marquee__track--clone');
        scroller.appendChild(clone);
        this.track.dataset.cloned = 'true';

        this.toggleButton = this.querySelector('[data-marquee-toggle]');
        if (!this.toggleButton) return;
        this.toggleButton.addEventListener('click', () => {
          const paused = this.classList.toggle('is-paused');
          this.toggleButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
          this.toggleButton.setAttribute(
            'aria-label',
            paused ? P.strings.playMarquee || 'Play' : P.strings.pauseMarquee || 'Pause'
          );
        });
      }
    }
  );

  /* ---------------- popup ---------------- */

  P.define(
    'popup-modal',
    class PopupModal extends Overlay {
      connectedCallback() {
        super.connectedCallback();
        this.key = `peachy:popup:${this.dataset.sectionId || 'default'}`;
        if (window.Shopify && window.Shopify.designMode) return;
        if (this.dismissed()) return;
        const delay = parseInt(this.dataset.delay || '6', 10) * 1000;
        this.timer = setTimeout(() => this.open(), delay);
        this.addEventListener('overlay:close', () => this.remember());
        const form = this.querySelector('form');
        if (form) form.addEventListener('submit', () => this.remember());
      }

      dismissed() {
        try {
          const stored = localStorage.getItem(this.key);
          if (!stored) return false;
          const days = parseInt(this.dataset.frequency || '7', 10);
          return Date.now() - parseInt(stored, 10) < days * 864e5;
        } catch (e) {
          return false;
        }
      }

      remember() {
        try {
          localStorage.setItem(this.key, String(Date.now()));
        } catch (e) {
          /* private mode */
        }
      }

      disconnectedCallback() {
        if (this.timer) clearTimeout(this.timer);
      }
    }
  );

  /* ---------------- share ---------------- */

  P.define(
    'share-button',
    class ShareButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;
        this.url = this.dataset.url || window.location.href;
        this.button.addEventListener('click', async () => {
          if (navigator.share) {
            try {
              await navigator.share({ title: document.title, url: this.url });
              return;
            } catch (e) {
              /* cancelled — fall through to copy */
            }
          }
          try {
            await navigator.clipboard.writeText(this.url);
            this.feedback();
          } catch (e) {
            /* clipboard blocked */
          }
        });
      }

      feedback() {
        const tip = document.createElement('span');
        tip.className = 'share-btn__tip';
        tip.setAttribute('role', 'status');
        tip.textContent = P.strings.copied || 'Copied';
        this.appendChild(tip);
        setTimeout(() => tip.remove(), 2200);
      }
    }
  );

  /* ---------------- facets auto-submit ---------------- */

  P.define(
    'facet-form',
    class FacetForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.form.addEventListener(
          'input',
          P.debounce((event) => {
            if (event.target.matches('input[type="number"], input[type="text"]')) return;
            this.submit();
          }, 120)
        );
        this.form.addEventListener('change', (event) => {
          if (event.target.matches('select')) this.submit();
        });
      }

      submit() {
        this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
      }
    }
  );

  /* ---------------- subscribe & save ---------------- */
  P.define(
    'selling-plans',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-plan-input]');
        this.select = this.querySelector('[data-plan-select]');
        this.radios = Array.from(this.querySelectorAll('[data-plan-mode]'));
        if (!this.input || !this.radios.length) return;

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-plan-mode]')) this.sync();
          else if (event.target === this.select) this.sync();
        });
        this.sync();
      }

      sync() {
        const active = this.radios.find((radio) => radio.checked);
        const subscribing = active ? active.dataset.planMode === 'subscribe' : false;

        /* A disabled input is left out of the POST, which is exactly what a
           one-time purchase needs — an empty selling_plan is still a value. */
        this.input.disabled = !subscribing;
        if (subscribing && this.select) this.input.value = this.select.value;

        for (const radio of this.radios) {
          radio.closest('.plan')?.classList.toggle('plan--active', radio.checked);
        }
        const frequency = this.querySelector('[data-plan-frequency]');
        if (frequency) frequency.hidden = !subscribing;
      }
    }
  );

  /* ---------------- tabs ---------------- */
  P.define(
    'tab-group',
    class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (!this.tabs.length) return;
        this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));

        this.addEventListener('click', (event) => {
          const tab = event.target.closest('[role="tab"]');
          if (tab) this.select(this.tabs.indexOf(tab));
        });

        this.addEventListener('keydown', (event) => {
          const current = this.tabs.indexOf(document.activeElement);
          if (current === -1) return;
          let next = null;
          if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === null) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        });
      }

      select(index) {
        if (index < 0 || index >= this.tabs.length) return;
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
          const panel = this.panels[i];
          if (panel) panel.hidden = !selected;
        });
      }
    }
  );

  /* ---------------- in-page jump links ---------------- */
  P.define(
    'anchor-nav',
    class extends HTMLElement {
      connectedCallback() {
        this.links = Array.from(this.querySelectorAll('[data-jump-link]'));
        if (!this.links.length) return;

        /* Drop links whose target isn't on this page rather than scrolling nowhere. */
        this.pairs = [];
        for (const link of this.links) {
          const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
          if (target) this.pairs.push({ link, target });
          else link.closest('li')?.remove();
        }
        if (!this.pairs.length) {
          this.hidden = true;
          return;
        }

        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-jump-link]');
          if (!link) return;
          const pair = this.pairs.find((item) => item.link === link);
          if (!pair) return;
          event.preventDefault();
          const headerHeight =
            parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          const offset = this.offsetHeight + headerHeight + 12;
          const top = pair.target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: Math.max(top, 0), behavior: P.scrollBehavior() });
          pair.target.setAttribute('tabindex', '-1');
          pair.target.focus({ preventScroll: true });
        });

        if (!('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              const pair = this.pairs.find((item) => item.target === entry.target);
              if (pair) this.mark(pair.link);
            }
          },
          { rootMargin: '-25% 0px -60% 0px' }
        );
        for (const pair of this.pairs) this.observer.observe(pair.target);
      }

      mark(active) {
        for (const pair of this.pairs) {
          const current = pair.link === active;
          pair.link.classList.toggle('is-current', current);
          if (current) pair.link.setAttribute('aria-current', 'true');
          else pair.link.removeAttribute('aria-current');
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    }
  );

  /* ---------------- reveal on scroll ---------------- */

  function initReveal() {
    if (!document.body.classList.contains('animate-reveal')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const targets = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!targets.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    for (const el of targets) observer.observe(el);
  }

  document.addEventListener('DOMContentLoaded', initReveal);
  document.addEventListener('shopify:section:load', initReveal);
  if (document.readyState !== 'loading') initReveal();

  /* Localisation forms submit on change, so their submit buttons can be hidden
     (CSS does that under .js). Without this the header showed a bare "Show
     results" button beside every selector. */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('select[name="country_code"], select[name="locale_code"]');
    if (!select) return;
    const form = select.form || select.closest('form');
    if (form) form.submit();
  });

  /* External links get an accessible hint */
  document.addEventListener('DOMContentLoaded', () => {
    for (const link of document.querySelectorAll('a[target="_blank"]')) {
      if (link.getAttribute('aria-describedby')) continue;
      link.setAttribute('aria-describedby', 'a11y-new-window-message');
    }
  });
})();
